import { useMutation } from "@tanstack/react-query";
import { useState } from "react";
import { put } from "../../lib/api";
import { limitsFromDraft, scopeLabel, validateLimitDraft } from "../../lib/apps";
import type { LimitDraftRow } from "../../lib/apps";
import { count } from "../../lib/format";
import type { AppCatalog, AppDetail, AppLimitScope, RateWindow } from "../../lib/types";
import { Button } from "../ui/Button";
import { ErrorNotice } from "../ui/Feedback";
import { CONTROL } from "../ui/Input";
import { MicroLabel, SectionTitle } from "../ui/Text";

/**
 * How often an app may call, as a grid: one row per permission plus one for all
 * requests together, one column per window.
 *
 * An empty cell means the default. For a permission that is the catalog's figure,
 * shown as the placeholder, so a grant is never unlimited by omission. For "All
 * requests" there is no default — an empty cell there is no app-wide cap.
 *
 * Minute and hour refill continuously; day and month reset at midnight in the
 * quota zone, and that zone is named under the grid.
 */

type Grid = Readonly<Record<string, string>>;

const cellKey = (scope: AppLimitScope, window: RateWindow) => `${scope}|${window}`;

function gridFrom(app: AppDetail): Grid {
  return Object.fromEntries(
    app.limits.map((limit) => [cellKey(limit.scope, limit.window), String(limit.max)]),
  );
}

export interface AppLimitsProps {
  readonly app: AppDetail;
  readonly catalog: AppCatalog;
  readonly canEdit: boolean;
  readonly onSaved: (message: string) => void;
}

export function AppLimits({ app, catalog, canEdit, onSaved }: AppLimitsProps) {
  const [grid, setGrid] = useState<Grid>(() => gridFrom(app));

  const scopes: readonly AppLimitScope[] = [
    ...catalog.permissions.map((entry) => entry.key),
    catalog.appWideScope,
  ];

  const rows: LimitDraftRow[] = scopes.flatMap((scope) =>
    catalog.windows
      .map((window) => ({ scope, window, max: grid[cellKey(scope, window)] ?? "" }))
      .filter((row) => row.max.trim() !== ""),
  );
  const issues = validateLimitDraft(rows);
  const issueAt = new Map(
    rows.map((row, index) => [cellKey(row.scope, row.window), issues[index]]),
  );
  const isValid = issues.every((issue) => issue === undefined);

  const saved = gridFrom(app);
  const isDirty = [...new Set([...Object.keys(saved), ...Object.keys(grid)])].some(
    (key) => (saved[key] ?? "").trim() !== (grid[key] ?? "").trim(),
  );

  const save = useMutation({
    mutationFn: () => put<AppDetail>(`/apps/${app.id}/limits`, { limits: limitsFromDraft(rows) }),
    onSuccess: () => onSaved("Limits saved. A lowered limit applies at once."),
  });

  function defaultFor(scope: AppLimitScope, window: RateWindow): number | undefined {
    return catalog.permissions.find((entry) => entry.key === scope)?.defaults[window];
  }

  return (
    <section aria-labelledby="app-limits" className="flex flex-col gap-4">
      <SectionTitle>
        <span id="app-limits">Rate limits</span>
      </SectionTitle>

      {save.isError ? <ErrorNotice error={save.error} /> : null}

      <div className="overflow-x-auto">
        <table className="w-full border-collapse">
          <thead>
            <tr className="border-b border-rule">
              <th className="py-2 pr-4 text-left">
                <MicroLabel>Requests per</MicroLabel>
              </th>
              {catalog.windows.map((window) => (
                <th key={window} className="py-2 pr-4 text-left">
                  <MicroLabel>{window}</MicroLabel>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {scopes.map((scope) => (
              <tr key={scope} className="border-b border-rule">
                <th scope="row" className="py-2 pr-4 text-left font-mono text-micro text-ink">
                  {scopeLabel(scope)}
                </th>
                {catalog.windows.map((window) => {
                  const key = cellKey(scope, window);
                  const fallback = defaultFor(scope, window);
                  const issue = issueAt.get(key);
                  return (
                    <td key={window} className="py-2 pr-4 align-top">
                      <input
                        aria-label={`${scopeLabel(scope)} per ${window}`}
                        inputMode="numeric"
                        disabled={!canEdit}
                        value={grid[key] ?? ""}
                        placeholder={fallback === undefined ? "no limit" : count(fallback)}
                        aria-invalid={issue !== undefined}
                        onChange={(event) => setGrid({ ...grid, [key]: event.target.value })}
                        className={`${CONTROL} max-w-32 ${issue === undefined ? "" : "border-signal"}`}
                      />
                      {issue === undefined ? null : (
                        <span
                          role="alert"
                          className="block text-micro tracking-normal normal-case text-signal"
                        >
                          {issue}
                        </span>
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <p className="m-0 max-w-prose text-micro tracking-normal normal-case text-ink-faint">
        Grey figures are the defaults and apply when a cell is empty. A request counts against its
        permission's row and the all-requests row; refused by either, it counts against neither.
        Days and months reset at midnight {catalog.timeZone}.
      </p>

      {canEdit ? (
        <div className="flex gap-3">
          <Button
            variant="primary"
            disabled={!isDirty || !isValid}
            pending={save.isPending}
            onClick={() => save.mutate()}
          >
            Save limits
          </Button>
          {isDirty ? (
            <Button variant="ghost" onClick={() => setGrid(saved)}>
              Undo
            </Button>
          ) : null}
        </div>
      ) : null}
    </section>
  );
}
